import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useToast } from '@retail/hooks';
import { mgPrisRoutes } from '@retail/mgpris/config';
import {
  CreateMonitoringAssortmentDialog,
  ImportAssortmentDialog,
  NewAssortment,
} from '@retail/monitoring-assortment/components';
import { useValidationOnImportedItems } from '@retail/monitoring-assortment/context';
import { useCreateMonitoringAssortment } from '@retail/monitoring-assortment/data-access';
import { useAssortmentTFunction } from '@retail/monitoring-assortment/i18n';
import { FetchError } from '@shared/fetch-utils';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

interface Props {
  openImportModal?: boolean;
  openCreateManuallyModal: boolean;
  toggleImportModal?: () => void;
  toggleCreateManuallyModal: () => void;
}

export function CreateMonitoringAssortment({
  openImportModal,
  openCreateManuallyModal,
  toggleImportModal,
  toggleCreateManuallyModal,
}: Props) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const t = useAssortmentTFunction();

  const {
    selectedOrgUnitAssortment: { orgUnitId },
  } = useSelectedOrgUnit();

  const { importedItems, validationState, resetImportedAssortmentStates, loadingValidateItems } =
    useValidationOnImportedItems();

  const { mutateAsync: createAssortmentAsync, isPending } = useCreateMonitoringAssortment();
  const submitCreateAssortment = (newAssortment: NewAssortment) => {
    createAssortmentAsync({
      body: {
        title: newAssortment.title,
        itemCodes: { mgItemNumbers: newAssortment.itemCodes?.mgItemNumbers || [] },
        orgUnitId,
      },
    })
      .then(async (assortment) => {
        queryClient.invalidateQueries({ queryKey: ['orgUnit-assortments'] });
        if (openImportModal && toggleImportModal) {
          toast.success(t('assortment.admin.create.success', { title: newAssortment.title }));
          toggleImportModal();
        } else {
          toggleCreateManuallyModal();
        }
        await navigate(
          mgPrisRoutes.marketInsight.assortments.assortment.getFullLinkPath({
            assortmentId: assortment.id,
          })
        );
      })
      .catch((err: FetchError) => {
        console.error(err);
        toast.error(t('assortment.admin.create.error.generic'));
      });
  };

  return (
    <>
      {openImportModal && toggleImportModal && (
        <ImportAssortmentDialog
          open={openImportModal}
          onClose={toggleImportModal}
          onSubmit={submitCreateAssortment}
          importedItems={importedItems}
          resetImportedAssortmentStates={resetImportedAssortmentStates}
          loadingValidateItems={loadingValidateItems}
          validationState={validationState}
          isLoading={isPending}
        />
      )}

      <CreateMonitoringAssortmentDialog
        open={openCreateManuallyModal}
        onClose={toggleCreateManuallyModal}
        onSubmit={submitCreateAssortment}
        isLoading={isPending}
      />
    </>
  );
}
